import * as React from 'react';
import { isEmpty } from 'lodash';

import VerticalSpacing from 'components/vertical-spacing/VerticalSpacing';
import CollapsibleSimple from 'components/collapsible/simple/CollapsibleSimple';

import ControlledRangeSelect from 'modules/discovery/components/range-select/ControlledRangeSelect';

import { abbrNum } from 'utils/helper/common';
import { FOLLOWERS_STEP_SELECTOR, VIEWS_STEP_SELECTOR, transformSteps } from 'modules/discovery/utils/discovery';
import { __t, applyTranslations } from 'utils/language';

import type {
  DiscoveryCreatorsFilter,
  DiscoveryCreatorsFilters,
  DiscoveryFilterMinMax
} from 'types';

import './discovery-creators-filters-youtube.scss';

applyTranslations(require('./discovery-creators-filters-youtube.i18n.json'));

type Props = {
  filters: DiscoveryCreatorsFilters,
  handleDiscoveryFiltersUpdate: (a: DiscoveryCreatorsFilter) => void
};

class DiscoveryCreatorsFiltersYoutube extends React.PureComponent<Props> {
  static defaultProps = {
    filters: {},
    handleDiscoveryFiltersUpdate: () => {}
  };

  handleUpdate = (result: DiscoveryCreatorsFilter): void => {
    const { handleDiscoveryFiltersUpdate } = this.props;

    if (handleDiscoveryFiltersUpdate) {
      handleDiscoveryFiltersUpdate(result);
    }
  };

  handleFollowersSliderChangeComplete = (range: DiscoveryFilterMinMax, name?: string | null) => {
    const { followers, engagement, views } = this.props.filters;

    if (!name) {
      return;
    }

    let newState = {};

    switch (name) {
      case 'followers':
        newState = {
          followers: {
            ...followers,
            youtube: {
              ...followers.youtube,
              // $FlowIgnore - Cannot determine a type for object literal [1]. DiscoveryFilterMinMax [2] is inexact
              ...range,
              transformed: {
                ...transformSteps(range, FOLLOWERS_STEP_SELECTOR)
              }
            }
          }
        };
        break;
      case 'engagement':
        newState = {
          engagement: {
            ...engagement,
            youtube: {
              ...engagement.youtube,
              // $FlowIgnore - Cannot determine a type for object literal [1]. DiscoveryFilterMinMax [2] is inexact
              ...range
            }
          }
        };
        break;
      case 'views':
        newState = {
          views: {
            ...views,
            youtube: {
              ...views.youtube,
              // $FlowIgnore - Cannot determine a type for object literal [1]. DiscoveryFilterMinMax [2] is inexact
              ...range,
              transformed: {
                ...transformSteps(range, VIEWS_STEP_SELECTOR)
              }
            }
          }
        };
        break;
      default:
        break;
    }

    this.handleUpdate(newState);
  };

  renderFollowersSlider(): React.ReactNode {
    const { filters } = this.props;

    const valueTransformer = (range) => {
      const transformedReach = transformSteps(range, FOLLOWERS_STEP_SELECTOR);

      const valueMin = abbrNum(transformedReach.min, { decimal: 1 });
      const valueMax = abbrNum(transformedReach.max, { decimal: 1 });
      return `${valueMin} – ${valueMax}`;
    };

    return (
      <ControlledRangeSelect
        key="youtube-followers-range"
        title={__t('discovery_creators_filters_youtube.subscribers_title')}
        tooltip={__t('discovery_creators_filters_youtube.subscribers_tooltip')}
        titleValueTransformer={valueTransformer}
        data={filters.followers.youtube}
        attribute="followers"
        onChangeComplete={this.handleFollowersSliderChangeComplete}
      />
    );
  }

  renderEngagementSlider(): React.ReactNode {
    const { filters } = this.props;

    const valueTransformer = (value) => `${value.min}% – ${value.max}%`;

    return (
      <ControlledRangeSelect
        key="youtube-engagement-range"
        title={__t('discovery_creators_filters_youtube.engagement_rate_title')}
        tooltip={__t('discovery_creators_filters_youtube.engagement_rate_tooltip')}
        titleValueTransformer={valueTransformer}
        data={filters.engagement.youtube}
        attribute="engagement"
        onChangeComplete={this.handleFollowersSliderChangeComplete}
      />
    );
  }

  renderViewsSlider(): React.ReactNode {
    const { filters } = this.props;

    const valueTransformer = (range) => {
      const transformedReach = transformSteps(range, VIEWS_STEP_SELECTOR);

      const valueMin = abbrNum(transformedReach.min, { decimal: 1 });
      const valueMax = abbrNum(transformedReach.max, { decimal: 1 });
      return `${valueMin} – ${valueMax}`;
    };

    return (
      <ControlledRangeSelect
        key="youtube-views-range"
        title={__t('discovery_creators_filters_youtube.average_video_views_title')}
        tooltip={__t('discovery_creators_filters_youtube.average_video_views_tooltip')}
        titleValueTransformer={valueTransformer}
        data={filters.views.youtube}
        attribute="views"
        onChangeComplete={this.handleFollowersSliderChangeComplete}
      />
    );
  }

  render(): React.ReactNode {
    const { filters } = this.props;
    const searchByName = filters.searchByName;

    return (
      <CollapsibleSimple
        title={__t('discovery_creators_filters_youtube.section_label')}
        contentClassName="discovery-creators-filters-youtube"
        isExpanded={isEmpty(searchByName)}
        isDisabled={!isEmpty(searchByName)}
      >
        {this.renderFollowersSlider()}
        <VerticalSpacing type="large" />
        {this.renderEngagementSlider()}
        <VerticalSpacing type="large" />
        {this.renderViewsSlider()}
        <VerticalSpacing type="tiny" />
      </CollapsibleSimple>
    );
  }
}

export default DiscoveryCreatorsFiltersYoutube;
